'use client'

import { useRouter } from 'next/navigation'
import { motion } from 'framer-motion'
import { Icons } from '@/components/ui/icons'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { useState, useMemo } from 'react'
import { Input } from '@/components/ui/input'

interface Agent {
  agent_name: string
  call_count: number
  average_score: number
  grade: 'A' | 'B' | 'C' | 'D'
  total_fatal_issues: number
}

interface AgentPerformanceProps {
  agents: Agent[]
  isLoading?: boolean
}

const GRADE_COLORS: Record<string, { ring: string; text: string; bg: string; bar: string }> = {
  A: { ring: 'border-emerald-400', text: 'text-emerald-600', bg: 'bg-emerald-50', bar: 'bg-emerald-500' },
  B: { ring: 'border-blue-400',    text: 'text-blue-600',    bg: 'bg-blue-50',    bar: 'bg-blue-500'    },
  C: { ring: 'border-amber-400',   text: 'text-amber-600',   bg: 'bg-amber-50',   bar: 'bg-amber-500'   },
  D: { ring: 'border-red-400',     text: 'text-red-600',     bg: 'bg-red-50',     bar: 'bg-red-500'     },
}

const AVATAR_COLORS = [
  'bg-blue-400', 'bg-purple-400', 'bg-emerald-400', 'bg-orange-400',
  'bg-pink-400', 'bg-teal-400', 'bg-rose-400', 'bg-indigo-400',
  'bg-cyan-400', 'bg-violet-400',
]

function getInitials(name: string): string {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('')
}

const PAGE_SIZE = 6
const SHOW_AVG_SCORE_CARD = false

export function AgentPerformance({ agents, isLoading = false }: AgentPerformanceProps) {
  const router = useRouter()
  const [search, setSearch] = useState('')
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc')
  const [page, setPage] = useState(1)

  const filtered = useMemo(() => {
    const s = search.toLowerCase()
    return agents
      .filter(a => a.agent_name.toLowerCase().includes(s))
      .sort((a, b) =>
        sortDir === 'desc'
          ? b.average_score - a.average_score
          : a.average_score - b.average_score
      )
  }, [agents, search, sortDir])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const totalCalls = agents.reduce((s, a) => s + a.call_count, 0)
  const avgScore = agents.length
    ? Math.round(agents.reduce((s, a) => s + a.average_score, 0) / agents.length)
    : 0

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className={cn('grid gap-4', SHOW_AVG_SCORE_CARD ? 'grid-cols-3' : 'grid-cols-2')}>
          {Array.from({ length: SHOW_AVG_SCORE_CARD ? 3 : 2 }).map((_, i) => (
            <Skeleton key={i} className='h-20 rounded-2xl' />
          ))}
        </div>
        <div className='rounded-2xl border bg-white'>
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className='h-16 m-3 rounded-xl' />)}
        </div>
      </div>
    )
  }

  const hasData = agents.length > 0

  return (
    <div className='space-y-4'>
      {/* Header */}
      <div>
        <h2 className='text-xl font-bold text-brand-navy'>Performance Overview</h2>
        <p className='text-sm text-muted-foreground mt-0.5'>
          Real-time analysis of agent interactions.
        </p>
      </div>

      {/* Stats cards */}
      <div className={cn('grid gap-4', SHOW_AVG_SCORE_CARD ? 'grid-cols-3' : 'grid-cols-2')}>
        <div className='flex items-center justify-between rounded-2xl bg-white border border-gray-100 px-5 py-4 shadow-sm'>
          <div>
            <p className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>Total Agents</p>
            <p className='text-3xl font-bold text-brand-navy mt-1'>{agents.length}</p>
          </div>
          <div className='flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50'>
            <Icons.users className='h-5 w-5 text-blue-500' strokeWidth={1.5} />
          </div>
        </div>

        <div className='flex items-center justify-between rounded-2xl bg-white border border-gray-100 px-5 py-4 shadow-sm'>
          <div>
            <p className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>Total Calls</p>
            <p className='text-3xl font-bold text-brand-navy mt-1'>{totalCalls}</p>
          </div>
          <div className='flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50'>
            <Icons.phone className='h-5 w-5 text-purple-500' strokeWidth={1.5} />
          </div>
        </div>

        {SHOW_AVG_SCORE_CARD && (
          <div className='flex items-center justify-between rounded-2xl bg-white border border-gray-100 px-5 py-4 shadow-sm'>
            <div>
              <p className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>Avg Score</p>
              <p className='text-3xl font-bold text-brand-navy mt-1'>{avgScore}%</p>
            </div>
            <div className='flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50'>
              <Icons.trendingUp className='h-5 w-5 text-emerald-500' strokeWidth={1.5} />
            </div>
          </div>
        )}
      </div>

      {/* Search + sort bar */}
      <div className='flex items-center gap-3'>
        <div className='relative flex-1'>
          <Icons.search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            placeholder='Search agents by name or ID...'
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            className='pl-9 h-10 bg-white rounded-xl border-gray-200'
          />
        </div>

        <div className='flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 h-10 text-sm text-muted-foreground'>
          <span className='whitespace-nowrap'>Sort by:</span>
          <select
            value={sortDir}
            onChange={e => { setSortDir(e.target.value as 'desc' | 'asc'); setPage(1) }}
            className='bg-transparent text-sm font-medium text-brand-navy focus:outline-none pr-1'
          >
            <option value='desc'>Performance Score (High to Low)</option>
            <option value='asc'>Performance Score (Low to High)</option>
          </select>
        </div>

        <button className='flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-muted-foreground hover:bg-gray-50 transition-colors'>
          <Icons.filter className='h-4 w-4' />
          Filters
        </button>

        <button
          onClick={() => router.push('/agents')}
          className='flex h-10 items-center gap-2 rounded-xl bg-brand-cornflower px-4 text-sm font-semibold text-white hover:bg-brand-navy transition-colors'
        >
          <Icons.arrowRight className='h-4 w-4' />
          View All
        </button>
      </div>

      {/* Table */}
      <div className='rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden'>
        {/* Column headers */}
        <div
          className='grid px-5 py-3 bg-gray-50 border-b border-gray-100 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'
          style={{ gridTemplateColumns: '2.5fr 100px 1fr 130px' }}
        >
          <span>Agent Details</span>
          <span>Calls</span>
          <span>Performance Metrics</span>
          <span className='text-right'>Overall Score</span>
        </div>

        {/* Rows */}
        <div className='divide-y divide-gray-50'>
          {!hasData ? (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
              <Icons.users className='mb-3 h-10 w-10 text-gray-200' />
              <p className='font-semibold text-brand-navy'>No agent data yet</p>
              <p className='mt-1 text-sm text-muted-foreground'>Assign calls to agents to see performance here.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-10'>
              <Icons.search className='mb-2 h-8 w-8 text-gray-200' />
              <p className='text-sm text-muted-foreground'>No agents match your search.</p>
            </div>
          ) : (
            paginated.map((agent, index) => {
              const globalIndex = (page - 1) * PAGE_SIZE + index
              const g = GRADE_COLORS[agent.grade]
              const avatarColor = AVATAR_COLORS[globalIndex % AVATAR_COLORS.length]
              const initials = getInitials(agent.agent_name)
              const hasFatal = agent.total_fatal_issues > 0

              return (
                <motion.div
                  key={agent.agent_name}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04, duration: 0.25 }}
                  onClick={() => router.push(`/agents/${encodeURIComponent(agent.agent_name)}`)}
                  className='grid items-center px-5 py-3.5 cursor-pointer hover:bg-gray-50/80 transition-colors'
                  style={{ gridTemplateColumns: '2.5fr 100px 1fr 130px' }}
                >
                  {/* Agent details */}
                  <div className='flex items-center gap-3 min-w-0'>
                    <div className='relative shrink-0'>
                      <div className={cn(
                        'flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold text-white',
                        avatarColor
                      )}>
                        {initials}
                      </div>
                      {/* Status dot */}
                      <span className={cn(
                        'absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white',
                        hasFatal ? 'bg-red-500' : 'bg-emerald-500'
                      )} />
                    </div>
                    <div className='min-w-0'>
                      <p className='truncate font-semibold text-brand-navy text-sm'>{agent.agent_name}</p>
                      <p className='text-xs text-muted-foreground'>
                        {hasFatal ? `${agent.total_fatal_issues} Fatal Issue${agent.total_fatal_issues > 1 ? 's' : ''}` : 'No Fatal Issues'}
                      </p>
                    </div>
                  </div>

                  {/* Calls badge */}
                  <div>
                    <span className='inline-flex items-center rounded-lg bg-gray-100 px-3 py-1 text-xs font-semibold text-brand-navy'>
                      {agent.call_count} Call{agent.call_count !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Performance metrics — two bars */}
                  <div className='pr-6 space-y-1.5'>
                    <div className='flex items-center gap-2'>
                      <span className='w-16 text-[11px] text-muted-foreground shrink-0'>Individual</span>
                      <div className='flex-1 h-2 rounded-full bg-gray-100 overflow-hidden'>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${agent.average_score}%` }}
                          transition={{ duration: 0.6, delay: index * 0.05 }}
                          className={cn('h-full rounded-full', g.bar)}
                        />
                      </div>
                      <span className='text-xs font-semibold text-muted-foreground w-8 text-right'>{agent.average_score}%</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <span className='w-16 text-[11px] text-muted-foreground shrink-0'>Collective</span>
                      <div className='flex-1 h-2 rounded-full bg-gray-100 overflow-hidden'>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${agent.average_score}%` }}
                          transition={{ duration: 0.6, delay: index * 0.05 + 0.1 }}
                          className={cn('h-full rounded-full opacity-70', g.bar)}
                        />
                      </div>
                      <span className='text-xs font-semibold text-muted-foreground w-8 text-right'>{agent.average_score}%</span>
                    </div>
                  </div>

                  {/* Overall score — grade circle + score */}
                  <div className='flex flex-col items-end gap-0.5'>
                    <div className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-full border-2 font-bold text-base',
                      g.ring, g.text, g.bg
                    )}>
                      {agent.grade}
                    </div>
                    <span className={cn('text-xs font-semibold', g.text)}>
                      {agent.average_score} Score
                    </span>
                  </div>
                </motion.div>
              )
            })
          )}
        </div>

        {/* Pagination footer */}
        {hasData && filtered.length > 0 && (
          <div className='flex items-center justify-between border-t border-gray-100 px-5 py-3'>
            <p className='text-xs text-muted-foreground'>
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)} to{' '}
              {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} results
            </p>
            <div className='flex items-center gap-1'>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className='flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-muted-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
              >
                <Icons.chevronLeft className='h-3.5 w-3.5' />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-lg text-xs font-semibold transition-colors',
                    p === page
                      ? 'bg-brand-cornflower text-white'
                      : 'border border-gray-200 text-muted-foreground hover:bg-gray-50'
                  )}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className='flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-muted-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
              >
                <Icons.chevronRight className='h-3.5 w-3.5' />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
